/**
 * Date utility functions for safe date handling throughout the application
 */

import { format, formatDistanceToNow } from 'date-fns';
import { ja } from 'date-fns/locale';

/**
 * Safely format a date string to display format
 * @param dateString - Date string to format
 * @param formatString - Format string (default: 'yyyy/MM/dd')
 * @returns Formatted date string or fallback message
 */
export const formatDateSafe = (
  dateString: string | null | undefined,
  formatString: string = 'yyyy/MM/dd'
): string => {
  if (!dateString) return '日付不明';

  try {
    const date = new Date(dateString);
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return '日付不明';
    }
    return format(date, formatString, { locale: ja });
  } catch (error) {
    console.warn('Invalid date format:', dateString, error);
    return '日付不明';
  }
};

/**
 * Safely format a date string to "time ago" format
 * @param dateString - Date string to format
 * @returns Formatted relative time string or fallback message
 */
export const formatDistanceToNowSafe = (
  dateString: string | null | undefined,
  options: { addSuffix?: boolean } = { addSuffix: true }
): string => {
  if (!dateString) return '不明';

  try {
    const date = new Date(dateString);
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return '不明';
    }
    return formatDistanceToNow(date, { ...options, locale: ja });
  } catch (error) {
    console.warn('Invalid date format:', dateString, error);
    return '不明';
  }
};

/**
 * Get the best available date from a document object
 * @param doc - Document object with various date fields
 * @returns Best available date string or null
 */
export const getBestDate = (doc: any): string | null => {
  return doc.uploadedAt ||
         doc.createdAt ||
         doc.updatedAt ||
         doc.lastModified ||
         null;
};

/**
 * Create a Date object from various date string formats safely
 * @param dateString - Date string to parse
 * @returns Valid Date object or null if invalid
 */
export const createDateSafe = (dateString: string | null | undefined): Date | null => {
  if (!dateString) return null;

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return null;
    }
    return date;
  } catch (error) {
    console.warn('Invalid date format:', dateString, error);
    return null;
  }
};

/**
 * Format file size in human-readable format
 * @param bytes - File size in bytes
 * @returns Formatted file size string
 */
export const formatFileSize = (bytes: number | undefined | null): string => {
  if (!bytes || bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};
